/* eslint-disable prettier/prettier */
/**
 * TaskFormDialog Component
 *
 * This component renders a dialog for creating or editing a task.
 * It handles task name, description, and steps, and provides functionality
 * for submitting and deleting tasks.
 */

import {
  Button,
  Checkbox,
  Dialog,
  Label,
  Paragraph,
  Popover,
  Spinner,
  Switch,
  Text,
  Theme,
  VisuallyHidden,
  XStack,
  YStack,
} from '@my/ui'
import { Check as CheckIcon, CircleHelp, Play, Trash, X } from '@tamagui/lucide-icons'
import { Task, TaskFormData } from 'app/data/types/tasks'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Control, Controller, useForm, useWatch } from 'react-hook-form'
import { Keyboard, ScrollView } from 'react-native'

import { useActiveProjects } from 'app/data/projects/useProjects'
import { useTimezone, useUser } from 'app/data/user/useUser'
import { useTimerStore } from 'app/features/focus/store'
import { useTaskActions } from 'app/shared/hooks/useTaskActions'
import { useTaskDialog } from 'app/shared/hooks/useTaskDialog'
import { Analytics, AnalyticsEvents } from 'app/utils/analytics/analytics'
import { useRouter } from 'solito/router'
import { DueDatePicker } from './DueDatePicker'
import { PriorityDropdown } from './PriorityDropdown'
import StepsManager from './StepsManager'
import { TaskDatePicker } from './TaskDatePicker'
import { TaskProjectPicker } from './TaskProjectPicker'
import { TaskTimePicker } from './TaskTimePicker'
import { TextAreaResize } from './TextAreaResize'

// Memoize child components
const MemoizedPriorityDropdown = React.memo(PriorityDropdown)
const MemoizedStepsManager = React.memo(StepsManager)

// Create a proper ref-forwarding themed trash icon
const ThemedTrash = React.forwardRef((props: any, ref) => {
  const { isHovered, ...iconProps } = props
  return <Trash ref={ref} {...iconProps} color={isHovered ? '$red10Light' : '$color6'} />
})

ThemedTrash.displayName = 'Delete'

// New SaveButton component
const SaveButton = ({
  onSubmit,
  isSaving,
  isDeleting,
  control,
}: {
  onSubmit: () => void
  isSaving: boolean
  isDeleting: boolean
  control: Control<TaskFormData>
}) => {
  const taskName = useWatch({ control, name: 'name' })
  const hasTaskName = taskName?.trim().length > 0

  return (
    <Theme>
      <Button
        color="$color1"
        backgroundColor={hasTaskName ? '$accentColor' : '$color6'}
        onPress={onSubmit}
        disabled={isSaving || isDeleting}
        borderRadius="$8"
        height="$3.5"
        marginTop="$3"
        fontSize={16}
        fontWeight={hasTaskName ? 'bold' : 'normal'}
        hoverStyle={{
          backgroundColor: hasTaskName ? '$accentColor' : '$color6',
          opacity: hasTaskName ? 0.8 : 1,
          borderColor: hasTaskName ? '$accentColor' : '$color6',
        }}
        pressStyle={{
          backgroundColor: hasTaskName ? '$accentColor' : '$color6',
          scale: 0.97,
          borderColor: hasTaskName ? '$accentColor' : '$color6',
        }}
      >
        {isSaving ? <Spinner color="$color1" /> : 'Save'}
      </Button>
    </Theme>
  )
}

// New DueDateSection component
const DueDateSection = ({ control }: { control: Control<TaskFormData> }) => {
  const dueDate = useWatch({ control, name: 'due_date' })
  const [showDueDate, setShowDueDate] = useState(!!dueDate)

  return (
    <XStack
      paddingLeft="$2"
      paddingRight="$2"
      marginTop="$4"
      marginBottom="$2"
      alignItems="center"
      alignSelf="flex-end"
      gap="$2"
    >
      <Label
        paddingRight="$0"
        minWidth={90}
        justifyContent="flex-end"
        size="$3"
        fontWeight="bold"
        color="$color8"
      >
        Hard Deadline:
      </Label>

      {!showDueDate && !dueDate ? (
        <XStack alignItems="center" gap="$2">
          <Switch
            size="$2"
            checked={showDueDate}
            onCheckedChange={setShowDueDate}
            cursor="pointer"
            backgroundColor="$color6"
          >
            <Switch.Thumb animation="quick" scale={0.7} backgroundColor="$color1" />
          </Switch>
        </XStack>
      ) : (
        <XStack alignItems="center" gap="$2">
          <Controller
            control={control}
            name="due_date"
            render={({ field: { onChange, value } }) => (
              <DueDatePicker value={value ?? null} onChange={onChange} placeholder="Set date" />
            )}
          />

          <Popover allowFlip placement="left" size="$2">
            <Popover.Trigger>
              <XStack cursor="pointer" hoverStyle={{ opacity: 0.7 }} animation="quick">
                <CircleHelp size={16} color="$accentColor" strokeWidth={2.5} />
              </XStack>
            </Popover.Trigger>
            <Theme name="dark">
              <Popover.Content
                enterStyle={{ y: -5, opacity: 0 }}
                exitStyle={{ y: -5, opacity: 0 }}
                animation="quick"
                padding="$3"
                borderWidth={1}
                backgroundColor="$color4"
                maxWidth={250}
                borderColor="$borderColor"
                elevate
              >
                <Popover.Arrow borderWidth={1} borderColor="$borderColor" />
                <YStack>
                  <Paragraph size="$2" lineHeight="$1">
                    For deadlines and dates you can&apos;t miss. Ari will help you plan ahead and
                    won&apos;t let you forget.
                  </Paragraph>
                </YStack>
              </Popover.Content>
            </Theme>
          </Popover>
        </XStack>
      )}
    </XStack>
  )
}

// Modified TaskTimePicker wrapper
const TimePickerWrapper = ({ control }: { control: Control<TaskFormData> }) => {
  const focusDate = useWatch({ control, name: 'focus_date' })

  return (
    <Controller
      control={control}
      name="duration"
      render={({ field: { value, onChange } }) => (
        <Controller
          control={control}
          name="focus_time"
          render={({ field: { value: focusTimeValue, onChange: focusTimeOnChange } }) => {
            return (
              <TaskTimePicker
                duration={value ?? null}
                focusTime={focusTimeValue ?? null}
                selectedDate={focusDate ?? null}
                onSave={(newDuration, newFocusTime) => {
                  onChange(newDuration)
                  focusTimeOnChange(newFocusTime)
                }}
              />
            )
          }}
        />
      )}
    />
  )
}

// At the top of the file
const logTaskDialog = (message: string, data?: any) => {
  //console.log(`[TaskFormDialog] ${message}`, data || '')
}

// Update the memo comparison to use hooks instead of props
const MemoizedTaskFormDialog = React.memo(TaskFormDialog, () => {
  // Always return true since we're using hooks now
  // Component will re-render when hook values change
  return true
})

export function TaskFormDialog() {
  const { defaultFocusDate, editingTask, visible: isDialogOpen, hide: closeDialog } = useTaskDialog()

  const { handleTaskSubmit, handleTaskDelete } = useTaskActions()

  const { control, handleSubmit, reset, getValues } = useForm<TaskFormData>()
  const { data: projects } = useActiveProjects()
  const router = useRouter()
  const { setActiveTask, start, activeTask } = useTimerStore()

  // State for tracking save and delete operations
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Add this new state to track focus
  const [focusedField, setFocusedField] = useState<'name' | 'description' | null>(null)

  // Add hover state
  const [isDeleteHovered, setIsDeleteHovered] = useState(false)

  // Add state to track if we should reset the form
  const [shouldReset, setShouldReset] = useState(false)

  // Step 2: Memoize expensive computations
  const initialSteps = useMemo(() => {
    return (
      editingTask?.steps?.map((step) => ({
        id: step.id,
        name: step.name,
        status: step.status,
        position: step.position,
        description: step.description || undefined,
      })) || []
    )
  }, [editingTask])

  // Find general project - add with other memoized values
  const generalProject = useMemo(() => {
    return projects?.find((project) => project.name === 'Unassigned')
  }, [projects])

  // Add a memoized default project id
  const defaultProjectId = useMemo(() => {
    return generalProject?.id || undefined
  }, [generalProject])

  // Memoize form reset handler
  const handleFormReset = useCallback(() => {
    if (editingTask) {
      // CRITICAL FIX: When editing a task, handle date precedence carefully
      // 1. If we have defaultFocusDate (e.g., from homepage context), use it
      // 2. Otherwise, use the task's existing focus_date
      // 3. This fixes the DatePicker bug where focus_date was in unexpected format
      const focusDateToUse = defaultFocusDate || editingTask.focus_date || null

      reset({
        name: editingTask.name,
        description: editingTask.description || '',
        steps:
          editingTask.steps?.map((step) => ({
            id: step.id,
            name: step.name,
            status: step.status,
            position: step.position,
            description: step.description || undefined,
          })) || [],
        status: editingTask.status === 'deleted' ? 'incomplete' : editingTask.status,
        priority: editingTask.priority || null,
        due_date: editingTask.due_date || null,
        focus_date: focusDateToUse, // Use the smart date selection logic
        focus_time: editingTask.focus_time || null,
        duration: editingTask.duration || null,
        position: editingTask.position,
        project_id: editingTask.project_id,
        initialSteps: initialSteps,
      })
    } else {
      // Creating new task - use defaultFocusDate from context
      reset({
        name: '',
        description: '',
        steps: [
          {
            name: '',
            status: 'incomplete',
            position: 0,
            description: '',
          },
        ],
        status: 'incomplete',
        priority: null,
        due_date: null,
        focus_date: defaultFocusDate || null,
        focus_time: '',
        duration: null,
        position: 0,
        project_id: defaultProjectId,
        initialSteps: [],
      })
    }
    setShouldReset(false)
  }, [editingTask, initialSteps, reset, defaultFocusDate, defaultProjectId])

  // Add this utility function inside the component, before other handlers
  const dismissKeyboard = useCallback(() => {
    Keyboard.dismiss()
  }, [])

  // Memoize focus handlers
  const handleFocus = useCallback((field: 'name' | 'description') => {
    setFocusedField(field)
  }, [])

  const handleBlur = useCallback(
    (onBlur: () => void) => {
      setFocusedField(null)
      onBlur()
      dismissKeyboard()
    },
    [dismissKeyboard]
  )

  // Memoize delete hover handlers
  const handleDeleteHoverIn = useCallback(() => {
    setIsDeleteHovered(true)
  }, [])

  const handleDeleteHoverOut = useCallback(() => {
    setIsDeleteHovered(false)
  }, [])

  // Update the useEffect to use memoized reset handler
  useEffect(() => {
    if (isDialogOpen) {
      handleFormReset()
    }
  }, [isDialogOpen, handleFormReset])

  // Memoize form submission handler
  const handleFormSubmit = useCallback(
    async (data: TaskFormData) => {
      if (!data.name.trim()) {
        console.error('Task name is required')
        return
      }

      const filteredData = {
        ...data,
        steps: (data.steps || []).filter((step) => step.name.trim() !== ''),
      }

      setIsSaving(true)

      try {
        await Promise.race([
          handleTaskSubmit(filteredData),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Submission timeout')), 30000)
          ),
        ])

        // Log analytics event
        Analytics.logEvent(
          editingTask ? AnalyticsEvents.TASK_UPDATED : AnalyticsEvents.TASK_CREATED,
          {
            task_name: data.name,
            has_description: !!data.description,
            has_due_date: !!data.due_date,
            has_focus_date: !!data.focus_date,
            has_focus_time: !!data.focus_time,
            priority: data.priority,
            steps_count: filteredData.steps?.length || 0,
            project_id: data.project_id,
          }
        )

        setIsSaving(false)
        closeDialog()
      } catch (error) {
        console.error('Failed to save task:', error)
        setIsSaving(false)
      }
    },
    [handleTaskSubmit, closeDialog, editingTask]
  )

  // Updated delete handler
  const handleDelete = useCallback(async () => {
    if (editingTask) {
      setIsDeleting(true)
      try {
        await Promise.race([
          handleTaskDelete(editingTask.id),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Deletion timeout')), 30000)
          ),
        ])

        // First update states
        setIsDeleting(false)
        setIsDeleteHovered(false)

        // Close the dialog
        closeDialog()
      } catch (error) {
        console.error('Failed to delete task:', error)
        setIsDeleting(false)
      }
    }
  }, [editingTask, handleTaskDelete, closeDialog])

  // Play task handler - start focus session
  const handlePlayTask = useCallback(() => {
    if (!editingTask) return

    const formData = getValues()
    const currentTask = {
      ...editingTask,
      ...formData,
    } as Task

    // Find project name
    const project = projects?.find(p => p.id === currentTask.project_id)
    const projectName = project?.name || 'Unassigned'

    // Check if this is a new task (different from currently active task)
    const isNewTask = activeTask?.id !== currentTask.id

    // Set active task in focus store with full task object
    setActiveTask(currentTask, projectName)

    // Only start the timer if it's a new task
    if (isNewTask) {
      start()
    }

    // Close dialog and navigate to focus
    closeDialog()
    router.push('/focus')
  }, [editingTask, getValues, projects, setActiveTask, start, activeTask, closeDialog, router])

  // Add this memoized handler after the other handlers
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setIsSaving(false)
        setIsDeleting(false)
        setIsDeleteHovered(false)
        setShouldReset(true)
        closeDialog()
      }
    },
    [closeDialog]
  )

  // Add useUser hook to get timezone directly
  const { timezone } = useTimezone()

  // Render the dialog
  return (
    <Dialog modal open={isDialogOpen && !shouldReset} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          animation="quick"
          opacity={0.9}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />

        <Dialog.Content
          bordered
          elevate
          key="task-form"
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          opacity={1}
          y={0}
          width="95%"
          maxWidth={600}
          backgroundColor="$color1"
          position="relative"
          maxHeight="90%"
        >
          <VisuallyHidden>
            <Dialog.Title>Task Form</Dialog.Title>
          </VisuallyHidden>

          <YStack flex={1} position="relative">
            <Dialog.Close asChild>
              <Button
                unstyled
                position="absolute"
                right="$1"
                top="$0.75"
                zIndex={1}
                cursor="pointer"
                hoverStyle={{ opacity: 0.7 }}
                animation="quick"
                pressStyle={{ scale: 0.9 }}
              >
                <X size={24} color="$color10" />
              </Button>
            </Dialog.Close>

            <ScrollView
              showsVerticalScrollIndicator
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: 80 }}
            >
              <YStack gap="$4" paddingTop="$3">
                <XStack alignItems="flex-start" gap="$2">
                  {editingTask && (
                    <Controller
                      control={control}
                      name="status"
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          checked={value === 'completed'}
                          onCheckedChange={(checked) =>
                            onChange(checked ? 'completed' : 'incomplete')
                          }
                          height={24}
                          width={24}
                          backgroundColor={value === 'completed' ? '#4AC985' : 'transparent'}
                          borderColor={value === 'completed' ? '#4AC985' : '$color5'}
                          borderRadius={1000}
                          borderWidth={2.5}
                          hoverStyle={{
                            borderColor: '#4AC985',
                          }}
                          pressStyle={{
                            borderColor: '#4AC985',
                            backgroundColor: '#4AC985',
                            scale: 0.95,
                          }}
                          cursor="pointer"
                          unstyled
                        >
                          <Checkbox.Indicator>
                            <CheckIcon
                              color="$color1"
                              size={18}
                              strokeWidth={4}
                              alignSelf="center"
                              marginTop="$0.5"
                            />
                          </Checkbox.Indicator>
                        </Checkbox>
                      )}
                    />
                  )}
                  <YStack flex={1} gap="$3" marginRight="$3">
                    <YStack
                      borderWidth={focusedField ? 0 : 0}
                      borderColor="$color5"
                      paddingLeft="$2"
                      borderRadius="$2"
                      gap="$2"
                    >
                      {/* Task name */}
                      <Controller
                        control={control}
                        name="name"
                        rules={{ required: true }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TextAreaResize
                            placeholder="Task name"
                            value={value ?? ''}
                            onChange={onChange}
                            onFocus={() => handleFocus('name')}
                            onBlur={() => handleBlur(onBlur)}
                            fontSize={20}
                            fontBold
                            padding={0}
                            backgroundColor="$color1"
                            borderWidth={0}
                            singleLine
                            maxRows={3}
                            paddingBottom="$2"
                            paddingRight="$2.5"
                            letterSpacing={-0.4}
                          />
                        )}
                      />

                      {/* Task description */}
                      <Controller
                        control={control}
                        name="description"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TextAreaResize
                            placeholder="Add Description"
                            value={value ?? ''}
                            onChange={onChange}
                            onFocus={() => handleFocus('description')}
                            onBlur={() => handleBlur(onBlur)}
                            fontSize={16}
                            padding={0}
                            backgroundColor="$color1"
                            borderWidth={0}
                            maxRows={8}
                            paddingBottom="$2"
                            letterSpacing={-0.3}
                          />
                        )}
                      />
                    </YStack>

                    {/* Controls and Steps Container */}
                    <YStack gap="$2">
                      {/* Date, Time, Priority controls row */}
                      <XStack
                        gap="$2.5"
                        flexWrap="wrap"
                        paddingLeft="$2"
                        paddingRight="$2"
                        marginBottom={-8}
                      >
                        <XStack marginBottom="$2">
                          <Controller
                            control={control}
                            name="focus_date"
                            render={({ field: { onChange, value } }) => (
                              <TaskDatePicker
                                value={value ?? null}
                                onChange={onChange}
                                placeholder="Set date"
                              />
                            )}
                          />
                        </XStack>

                        <XStack marginBottom="$2">
                          <TimePickerWrapper control={control} />
                        </XStack>

                        <XStack marginBottom="$2">
                          <Controller
                            control={control}
                            name="priority"
                            render={({ field: { onChange, value } }) => (
                              <MemoizedPriorityDropdown value={value ?? null} onChange={onChange} />
                            )}
                          />
                        </XStack>

                        <XStack marginBottom="$2">
                          <Controller
                            control={control}
                            name="project_id"
                            render={({ field: { onChange, value } }) => (
                              <TaskProjectPicker projectId={value} onChange={onChange} />
                            )}
                          />
                        </XStack>
                      </XStack>

                      {/* Add new row for DueDatePicker */}
                      <DueDateSection control={control} />

                      {/* Steps manager below */}
                      <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator>
                        <Controller
                          control={control}
                          name="steps"
                          render={({ field: { onChange, value } }) => (
                            <MemoizedStepsManager steps={value || []} setSteps={onChange} />
                          )}
                        />
                      </ScrollView>
                    </YStack>
                  </YStack>
                </XStack>
              </YStack>
            </ScrollView>

            {/* Fixed bottom action bar */}
            <XStack
              gap="$3"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              backgroundColor="$color1"
              paddingHorizontal="$3"
              paddingVertical="$3"
            >
              {/* Left side - Delete button */}
              <XStack width="$12" justifyContent="flex-start">
                {editingTask && (
                  <Button
                    onPress={handleDelete}
                    disabled={isSaving || isDeleting}
                    borderRadius="$8"
                    borderWidth={2}
                    borderColor={isDeleteHovered ? '$red10Light' : '$color5'}
                    backgroundColor="transparent"
                    width="$6"
                    height="$3.5"
                    marginTop="$3"
                    onHoverIn={handleDeleteHoverIn}
                    onHoverOut={handleDeleteHoverOut}
                    pressStyle={{
                      backgroundColor: 'transparent',
                      scale: 0.97,
                      borderColor: '$red10Light',
                    }}
                    hoverStyle={{
                      backgroundColor: 'transparent',
                      borderColor: '$red10Light',
                    }}
                  >
                    {isDeleting ? (
                      <Spinner color="$red10Light" />
                    ) : (
                      <ThemedTrash size={18} strokeWidth={2.5} isHovered={isDeleteHovered} />
                    )}
                  </Button>
                )}
              </XStack>

              {/* Center - Focus button */}
              <XStack flex={1} justifyContent="center">
                {editingTask && (
                  <Button
                    onPress={handlePlayTask}
                    disabled={isSaving || isDeleting}
                    borderRadius="$8"
                    borderWidth={2}
                    //borderColor="$accentColor"
                    backgroundColor="$color3"
                    height="$3.5"
                    marginTop="$3"
                    paddingHorizontal="$4"
                    minWidth="$10"
                    pressStyle={{
                      backgroundColor: 'transparent',
                      scale: 0.97,
                      borderColor: '$accentColor',
                      opacity: 0.7,
                    }}
                    hoverStyle={{
                      backgroundColor: 'transparent',
                      borderColor: '$accentColor',
                      opacity: 0.8,
                    }}
                  >
                    <XStack alignItems="center" gap="$2">
                      <Text color="$accentColor" fontWeight="600" fontSize="$4">
                        Focus
                      </Text>
                      <Play size={18} strokeWidth={2.5} color="$accentColor" />
                    </XStack>
                  </Button>
                )}
              </XStack>

              {/* Right side - Save button */}
              <XStack width="$12" justifyContent="flex-end">
                <SaveButton
                  onSubmit={handleSubmit(handleFormSubmit)}
                  isSaving={isSaving}
                  isDeleting={isDeleting}
                  control={control}
                />
              </XStack>
            </XStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
